import {
  Camera,
  CameraDirection,
  CameraResultType,
  CameraSource,
  Photo,
} from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';

import {
  captureCropPictureWithWebCamera,
  isWebCameraCaptureAvailable,
  WebCameraCaptureOptions,
} from './capture-crop-picture-web-camera';

/** Native Capacitor path — OS resizes before handing bytes to the WebView. */
const NATIVE_CAMERA_MAX_DIMENSION = 1920;
const NATIVE_CAMERA_JPEG_QUALITY = 90;

export type CropPictureSource = 'camera' | 'gallery';

export type PickCropPictureOptions = WebCameraCaptureOptions;

/**
 * Prefer the in-app web camera on mobile PWA, or Capacitor Camera on native.
 * Avoids HTML <input capture> full-res files that OOM the tab.
 */
export function isCapacitorCameraPickerAvailable(): boolean {
  if (Capacitor.isNativePlatform()) {
    return true;
  }

  if (typeof navigator === 'undefined') {
    return false;
  }

  // Desktop browsers keep the file input; in-app camera is for phones in the field.
  const mobileUa = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
  return mobileUa && isWebCameraCaptureAvailable();
}

/** Resolves true once a camera picker path is ready. */
export function whenCapacitorCameraPickerAvailable(): Promise<boolean> {
  if (isCapacitorCameraPickerAvailable()) {
    return Promise.resolve(true);
  }

  return Promise.resolve(false);
}

function isUserCancelled(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return /cancel/i.test(message) || /dismiss/i.test(message);
}

async function capacitorPhotoToFile(photo: Photo): Promise<File> {
  const format = photo.format || 'jpeg';
  const extension = format === 'jpeg' ? 'jpg' : format;
  const mimeType = `image/${extension === 'jpg' ? 'jpeg' : extension}`;
  const fileName = `crop-picture.${extension}`;

  if (photo.webPath) {
    const response = await fetch(photo.webPath);
    const blob = await response.blob();
    return new File([blob], fileName, {
      type: blob.type || mimeType,
      lastModified: Date.now(),
    });
  }

  if (photo.base64String) {
    const binary = atob(photo.base64String);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new File([bytes], fileName, {
      type: mimeType,
      lastModified: Date.now(),
    });
  }

  throw new Error('Empty camera result');
}

async function pickNativeCameraPicture(): Promise<File | null> {
  await Camera.requestPermissions({
    permissions: ['camera'],
  });

  const photo = await Camera.getPhoto({
    quality: NATIVE_CAMERA_JPEG_QUALITY,
    width: NATIVE_CAMERA_MAX_DIMENSION,
    height: NATIVE_CAMERA_MAX_DIMENSION,
    source: CameraSource.Camera,
    direction: CameraDirection.Rear,
    saveToGallery: false,
    allowEditing: false,
    resultType: CameraResultType.Uri,
    correctOrientation: true,
  });

  return capacitorPhotoToFile(photo);
}

/**
 * Opens the camera. On PWA uses a custom rear-camera capture (avoids pwa-elements
 * ultrawide / washed preview). On native uses Capacitor Camera.
 */
export async function pickCropPicture(
  source: CropPictureSource,
  options: PickCropPictureOptions = {}
): Promise<File | null> {
  if (source !== 'camera') {
    return null;
  }

  if (!isCapacitorCameraPickerAvailable()) {
    return null;
  }

  try {
    if (Capacitor.isNativePlatform()) {
      return await pickNativeCameraPicture();
    }

    return await captureCropPictureWithWebCamera(options);
  } catch (error) {
    if (isUserCancelled(error)) {
      return null;
    }
    throw error;
  }
}
